import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { memo, useMemo } from 'react'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import Button from '@nxt-insurance/ui-kit/components/Button'

import { generateSrcSet, urlFor } from '@/cms/client'
import type { Section } from '@/cms/sections'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { getLocaleParts } from '@/utils/locale'
import type { Language } from '@constants/format'
import { SCREEN_SIZE } from '@ui-kit/constants'
import { body1, body2, h1 } from '@ui-kit/core/typography'
import type { Color } from '@ui-kit/types'

const HAPPY_CUSTOMER_WIDTH_MOBILE = 120

const HAPPY_CUSTOMER_WIDTH_EN = 140
const HAPPY_CUSTOMER_WIDTH_DE = 170

const WIDGET_HEIGHT = 108

const BG_IMAGE_WIDTH = 600
const BG_IMAGE_HEIGHT = 800

type HeaderTemplateSectionT = Exclude<
  Extract<
    Section,
    {
      name: string | null
      productTitle: string
    }
  >,
  { _type: 'website.richContent.section' }
>

type ImageLanguageMap = Record<
  Language,
  {
    darkGreen: string
    blue: string
    lightGrey: string
  }
>
const getVariantColor = (colorTheme: string) => {
  if (colorTheme === 'darkGreen') {
    return 'brandLime'
  } else if (colorTheme === 'blue') {
    return 'playground3Dark'
  }
  return 'brandDark'
}

const imageLanguageMap: ImageLanguageMap = {
  de: {
    darkGreen: 'customer_number_light_de',
    blue: 'customer_number_dark_de',
    lightGrey: 'customer_number_dark_de',
  },
  en: {
    darkGreen: 'customer_number_light_en',
    blue: 'customer_number_dark_en',
    lightGrey: 'customer_number_dark_en',
  },
}

const imageWidthMap = {
  de: HAPPY_CUSTOMER_WIDTH_DE,
  en: HAPPY_CUSTOMER_WIDTH_EN,
}

const getImageWidth = (language: Language) => {
  return imageWidthMap[language] ?? 170
}

const getCustomerNumberImagePath = (
  colorTheme: keyof ImageLanguageMap[Language],
  language: Language
) => {
  return `/assets/images/svg/${
    imageLanguageMap[language][colorTheme] ?? 'customer_number_dark_en'
  }.svg`
}

const headerSection = tv({
  slots: {
    container:
      'my-10 flex h-auto flex-col items-center bg-brand-dark pt-16 md:mx-16 md:rounded-3xl xl:mx-auto xl:w-[1270px]',
    contentWrapper:
      'mx-auto flex h-full flex-col items-center sm:gap-y-6 md:gap-y-8 xl:w-1/2 xl:pl-12',
    iconContainer: 'py-10 xl:py-14 xl:pl-10',
    backgroundImageContainer: 'max-w-1/2 max-h-[900px] overflow-hidden rounded-r-3xl',
    buttonWrapper: 'flex items-center gap-y-2 pb-2 pt-16 ',
    overline: [
      body1({ bold: true }),
      'flex text-brand-lime sm:px-10 sm:text-center md:px-0 xl:text-left ',
    ],
    productTitle: [h1({ type: 'primary' }), 'text-ui-light sm:text-center xl:text-left'],
    productDescription: [body1(), 'text-ui-light sm:text-center xl:text-left'],
    bottomCTA: [body2(), 'text-ui-light/70 sm:text-center xl:text-left'],
  },
  variants: {
    isBackgroundImageOn: {
      true: {
        container: 'pt-0 xl:flex-row',
        contentWrapper: 'items-start gap-y-4 pt-8',
        iconContainer: 'xl:justify-start xl:pl-0',
        buttonWrapper: 'items-start pt-12',
      },
    },
    color: {
      blue: {
        container: 'bg-playground3-light',
        overline: 'text-ui-dark/70',
        productTitle: 'text-playground3-dark',
        productDescription: 'text-ui-dark/70',
        bottomCTA: 'text-ui-dark/70',
      },
      lightGrey: {
        container: 'bg-ui-grey-50',
        overline: 'text-ui-dark/70',
        productTitle: 'text-playground3-dark',
        productDescription: 'text-ui-dark/70',
        bottomCTA: 'text-ui-dark/70',
      },
      darkGreen: {},
    },
  },
})

const colorThemeSchema = z.enum(['blue', 'lightGrey', 'darkGreen'])

export const HeaderSection = memo(
  (props: { section: HeaderTemplateSectionT; backgroundColor?: Color; index: number }) => {
    const { locale } = useRouter()
    const [lang] = getLocaleParts(locale)

    const { track } = useAnalytics()
    const windowWidth = useWindowWidth()
    const isMobile = windowWidth < SCREEN_SIZE.M
    const buttonLabel = props.section.ctaLabel
    const happyCustomerWidth = isMobile ? HAPPY_CUSTOMER_WIDTH_MOBILE : getImageWidth(lang)

    const variantColor = getVariantColor(props.section.colorTheme)
    const isBackgroundImage = props.section.backgroundImage !== null

    const shouldShowBackGroundImage = windowWidth > SCREEN_SIZE.XL && isBackgroundImage

    const {
      container,
      contentWrapper,
      iconContainer,
      backgroundImageContainer,
      buttonWrapper,
      overline,
      productTitle,
      productDescription,
      bottomCTA,
    } = headerSection({
      isBackgroundImageOn: shouldShowBackGroundImage,
      color: colorThemeSchema.parse(props.section.colorTheme),
    })

    const happyCustomersImgPath = getCustomerNumberImagePath(
      colorThemeSchema.parse(props.section.colorTheme),
      lang
    )

    const backgroundImageProps = useMemo(() => {
      if (!props.section.backgroundImage?.asset) {
        return
      }
      const url = urlFor(props.section.backgroundImage)
      return {
        src: url.url(),
        srcSet: generateSrcSet(url),
        loader: ({ src }: { src: string }) => src,
      }
    }, [props.section.backgroundImage])

    const flowHref = `https://start.hellogetsafe.com/${locale}/flows/${props.section.flowName}`

    const preparedContent = props.section.productDescription
      .split('\n')
      // @ts-expect-error
      .reduce((prev, curr, index) => [...prev, <br key={`br-${curr.toString()}-${index}`} />, curr])

    return shouldShowBackGroundImage ? (
      <View className={container()}>
        <View className={contentWrapper()}>
          <Text className={overline()}>{props.section.overline}</Text>
          <Text className={productTitle()}>{props.section.productTitle}</Text>
          <Text className={productDescription()}>{preparedContent}</Text>
          <View className={buttonWrapper()}>
            <Link
              href={flowHref}
              aria-label={buttonLabel}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track(EVENTS.LinkClicked, {
                  flowHref,
                  sectionName: props.section.name,
                })
              }}
            >
              <Button
                label={buttonLabel}
                type={'default'}
                variantColor={variantColor ?? 'brandLime'}
              ></Button>
            </Link>
            <Text className={bottomCTA()}>{props.section.ctaSupportText}</Text>
          </View>
          <View className={iconContainer()}>
            <Image
              src={happyCustomersImgPath}
              width={happyCustomerWidth}
              height={WIDGET_HEIGHT}
              loader={({ src }) => src}
              unoptimized
              alt="500,000 happy customers"
            />
          </View>
        </View>
        <View className={backgroundImageContainer()}>
          <Image
            src={backgroundImageProps?.src ?? '/assets/images/logo_black.svg'}
            loader={({ src }) => src}
            height={BG_IMAGE_HEIGHT}
            width={BG_IMAGE_WIDTH}
            alt=""
            style={{
              objectFit: 'cover',
            }}
          />
        </View>
      </View>
    ) : (
      <View className={container()}>
        <View className={contentWrapper()}>
          <Text className={overline()}>{props.section.overline}</Text>
          <Text className={productTitle()}>{props.section.productTitle}</Text>
          <Text className={productDescription()}>{preparedContent}</Text>
          <View className={buttonWrapper()}>
            <Link
              href={flowHref}
              aria-label={buttonLabel}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track(EVENTS.LinkClicked, {
                  flowHref,
                  sectionName: props.section.name,
                })
              }}
            >
              <Button
                label={buttonLabel}
                type={'default'}
                variantColor={variantColor ?? 'brandLime'}
              ></Button>
            </Link>
            <Text className={bottomCTA()}>{props.section.ctaSupportText}</Text>
          </View>
        </View>

        <View className={iconContainer()}>
          <Image
            src={happyCustomersImgPath}
            width={happyCustomerWidth}
            height={WIDGET_HEIGHT}
            loader={({ src }) => src}
            unoptimized
            alt="500,000 happy customers"
          />
        </View>
      </View>
    )
  }
)
